"use client";
import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  ArrowLeft,
  ArrowRight,
  FileUp,
  Plus,
  RefreshCw,
  Search,
  TableProperties
} from "lucide-react";
import { RequireRole } from "../console-shell";
import { Banner, Button, Desk, Fact, Facts, PageHeader, StatusTag } from ".";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { ReadState, useRead, type PageResult } from "./record-tools";
import { BatchEditor, MappingEditor, StageEditor } from "./import-editors";
import { ImportReport } from "./import-report";
import {
  BATCH_LABEL,
  BATCH_TONE,
  FIELD_LABEL,
  batchCounts,
  type Batch,
  type Mapping,
  type Report
} from "./import-model";
import { formatTime } from "../../lib/api";
export function ImportWorkspace() {
  return (
    <RequireRole roleCode="PROFILE_CASE_HANDLER">
      <Suspense fallback={<Desk>正在读取导入工作区…</Desk>}>
        <ImportDesk />
      </Suspense>
    </RequireRole>
  );
}
function ImportDesk() {
  const params = useSearchParams(),
    [tab, setTab] = useState(params.get("tab") === "mappings" ? "mappings" : "batches"),
    [editor, setEditor] = useState<"stage" | "mapping" | "commit" | "discard" | null>(null),
    [selected, setSelected] = useState<string | null>(params.get("batch")),
    [mappingId, setMappingId] = useState<string | null>(null),
    [message, setMessage] = useState("");
  const [word, setWord] = useState(""),
    [q, setQ] = useState(""),
    [status, setStatus] = useState(""),
    [page, setPage] = useState(0),
    [cursors, setCursors] = useState([""]);
  const focus = useRef<HTMLElement | null>(null),
    heading = useRef<HTMLHeadingElement>(null);
  const list = useRead<PageResult<Batch>>(
      `/api/v1/member-imports?${new URLSearchParams({ limit: "20", ...(q ? { q } : {}), ...(status ? { status } : {}), ...(cursors[page] ? { cursor: cursors[page] } : {}) })}`
    ),
    maps = useRead<{ items: Mapping[] }>(
      tab === "mappings" ? "/api/v1/member-imports/mappings" : null
    ),
    read = useRead<Report>(
      selected ? `/api/v1/member-imports/${encodeURIComponent(selected)}` : null
    );
  const mapping = maps.data?.items.find((m) => m.id === mappingId);
  useEffect(() => {
    const id = params.get("batch");
    if (id) setSelected(id);
  }, [params]);
  const reset = () => {
    setPage(0);
    setCursors([""]);
  };
  const saved = (value: unknown) => {
    const action = editor;
    setEditor(null);
    if (action === "stage") {
      const report = value as Report;
      setSelected(report.batch.id);
      setMessage("预检报告已保存，请核对后决定是否入库。");
      reset();
      void list.reload();
    } else if (action === "mapping") {
      setTab("mappings");
      setMessage("字段对应已登记，可在新建导入时选用。");
      void maps.reload();
    } else {
      const result = value as { applied?: number; failures?: unknown[] };
      setMessage(
        action === "discard"
          ? "批次已作废，原始行与对账记录继续保留。"
          : `入库办理已结束：本次完整处理 ${result.applied ?? 0} 行，入库时失败 ${result.failures?.length ?? 0} 行。请在报告中核对结果。`
      );
      void read.reload();
      void list.reload();
    }
  };
  if (editor === "stage")
    return (
      <Desk>
        <StageEditor onCancel={() => setEditor(null)} onSaved={saved} />
      </Desk>
    );
  if (editor === "mapping")
    return (
      <Desk>
        <MappingEditor onCancel={() => setEditor(null)} onSaved={saved} />
      </Desk>
    );
  if ((editor === "commit" || editor === "discard") && read.data)
    return (
      <Desk>
        <BatchEditor
          key={read.data.batch.id + editor}
          report={read.data}
          action={editor}
          onCancel={() => setEditor(null)}
          onSaved={saved}
        />
      </Desk>
    );
  if (selected) {
    if (read.data)
      return (
        <ImportReport
          key={read.data.batch.id + ":" + read.data.batch.version}
          report={read.data}
          message={message}
          reload={read.reload}
          onBack={() => {
            setSelected(null);
            setMessage("");
          }}
          onAction={setEditor}
        />
      );
    return (
      <Desk>
        <Button variant="ghost" className="record-back" onClick={() => setSelected(null)}>
          <ArrowLeft size={16} />
          返回历史批次
        </Button>
        <PageHeader
          eyebrow="会员数据导入 / 对账报告"
          title="读取批次报告"
          description="读取来源、逐行结果与本批次的当前状态。"
        />
        {message && <Banner tone="success">{message}</Banner>}
        <ReadState {...read} retry={read.reload} />
      </Desk>
    );
  }
  return (
    <Desk wide>
      <PageHeader
        eyebrow="会员与组织 / 数据导入"
        title="会员数据导入"
        description="按批次核对来源、检查问题，再将通过预检的数据写入会员档案。"
        actions={
          <Button
            onClick={() => {
              setMessage("");
              setEditor("stage");
            }}
          >
            <Plus size={16} />
            新建导入
          </Button>
        }
      />
      {message && <Banner tone="success">{message}</Banner>}
      <nav className="record-tabs" aria-label="导入工作区">
        <button
          aria-current={tab === "batches" ? "page" : undefined}
          onClick={() => setTab("batches")}
        >
          历史批次
        </button>
        <button
          aria-current={tab === "mappings" ? "page" : undefined}
          onClick={() => setTab("mappings")}
        >
          字段对应
        </button>
      </nav>
      {tab === "batches" ? (
        <>
          <div className="access-filters" aria-label="导入批次状态">
            {[["", "全部"], ...Object.entries(BATCH_LABEL)].map(([value, label]) => (
              <button
                key={value}
                aria-pressed={status === value}
                onClick={() => {
                  setStatus(value);
                  reset();
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="record-toolbar">
            <form
              className="record-search"
              onSubmit={(e) => {
                e.preventDefault();
                setQ(word.trim());
                reset();
              }}
            >
              <Search size={17} />
              <input
                aria-label="搜索来源或字段对应名称"
                placeholder="来源或字段对应名称"
                value={word}
                onChange={(e) => setWord(e.target.value)}
                maxLength={60}
              />
              <Button type="submit" variant="outline">
                查找
              </Button>
            </form>
            <Button variant="ghost" aria-label="刷新导入批次" onClick={list.reload}>
              <RefreshCw size={16} />
            </Button>
          </div>
          <ReadState {...list} retry={list.reload} />
          {list.data && (
            <>
              <div className="record-list-meta">
                <span>当前页 {list.data.items.length} 个批次</span>
                <span>按预检时间由近到远排列</span>
              </div>
              {list.data.items.length ? (
                <div className="record-table-scroll">
                  <table className="record-table import-batch-table">
                    <caption className="sr-only">会员导入历史批次</caption>
                    <thead>
                      <tr>
                        <th>来源与字段对应</th>
                        <th>当前状态</th>
                        <th>行数与处理结果</th>
                        <th>预检时间</th>
                        <th>
                          <span className="sr-only">报告</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.data.items.map((b) => (
                        <tr key={b.id}>
                          <td>
                            <button
                              className="import-person"
                              onClick={() => {
                                setMessage("");
                                setSelected(b.id);
                              }}
                            >
                              {b.sourceRef}
                            </button>
                            <small>{b.mappingName}</small>
                          </td>
                          <td>
                            <StatusTag tone={BATCH_TONE[b.status]}>
                              {BATCH_LABEL[b.status]}
                            </StatusTag>
                          </td>
                          <td>
                            {b.rowCount} 行<small>{batchCounts(b)}</small>
                          </td>
                          <td>{formatTime(b.recordedAt)}</td>
                          <td>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`查看${b.sourceRef}的报告`}
                              onClick={() => {
                                setMessage("");
                                setSelected(b.id);
                              }}
                            >
                              <ArrowRight size={15} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="workspace-empty">
                  <FileUp />
                  <strong>没有匹配的导入批次</strong>
                  <p>
                    {q || status
                      ? "调整状态或关键词后再查找。"
                      : "从新建导入开始，预检报告会保存在这里。"}
                  </p>
                </div>
              )}
              <div className="record-pagination">
                <span>第 {page + 1} 页</span>
                <Button variant="outline" disabled={!page} onClick={() => setPage(page - 1)}>
                  上一页
                </Button>
                <Button
                  variant="outline"
                  disabled={!list.data.meta?.nextCursor}
                  onClick={() => {
                    setCursors((old) => [...old.slice(0, page + 1), list.data!.meta!.nextCursor!]);
                    setPage(page + 1);
                  }}
                >
                  下一页
                </Button>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="record-toolbar">
            <p className="record-muted">保存常用表头与导入字段的对应规则，供后续批次选用。</p>
            <Button variant="outline" onClick={() => setEditor("mapping")}>
              <Plus size={16} />
              登记字段对应
            </Button>
          </div>
          <ReadState {...maps} retry={maps.reload} />
          {maps.data && (
            <>
              <div className="record-list-meta">
                <span>已登记 {maps.data.items.length} 份对应规则</span>
              </div>
              {maps.data.items.length ? (
                <div className="record-table-scroll">
                  <table className="record-table import-mappings">
                    <caption className="sr-only">已登记的字段对应规则</caption>
                    <thead>
                      <tr>
                        <th>规则名称</th>
                        <th>对应列数</th>
                        <th>来源依据</th>
                        <th>查看</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maps.data.items.map((m) => (
                        <tr key={m.id}>
                          <td>{m.name}</td>
                          <td>{Object.keys(m.columns).length} 列</td>
                          <td>{m.sourceRef}</td>
                          <td>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`查看${m.name}的字段对应`}
                              onClick={() => {
                                focus.current = document.activeElement as HTMLElement;
                                setMappingId(m.id);
                              }}
                            >
                              <ArrowRight size={15} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="workspace-empty">
                  <TableProperties />
                  <strong>尚未登记字段对应</strong>
                  <p>先说明源文件的表头对应哪些字段，再用于导入。</p>
                </div>
              )}
            </>
          )}
        </>
      )}
      <h2 className="sr-only" tabIndex={-1} ref={heading}>
        导入工作区
      </h2>
      <Sheet
        open={!!mapping}
        onOpenChange={(open) => {
          if (!open) setMappingId(null);
        }}
      >
        <SheetContent
          className="record-sheet import-row-sheet workspace"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            (focus.current?.isConnected ? focus.current : heading.current)?.focus();
          }}
        >
          <SheetHeader>
            <p className="record-eyebrow">字段对应规则</p>
            <SheetTitle>{mapping?.name}</SheetTitle>
            <SheetDescription>核对源文件的表头与可导入字段。</SheetDescription>
          </SheetHeader>
          {mapping && (
            <div className="record-sheet-body">
              <section className="record-section">
                <Facts>
                  <Fact label="来源依据">{mapping.sourceRef}</Fact>
                  <Fact label="规则版本">第 {mapping.version} 版</Fact>
                </Facts>
              </section>
              <section className="record-section">
                <h2>逐列对应</h2>
                <Facts>
                  {Object.entries(mapping.columns).map(([column, field]) => (
                    <Fact key={column} label={column}>
                      {FIELD_LABEL[field] ?? "未识别字段"}
                    </Fact>
                  ))}
                </Facts>
              </section>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Desk>
  );
}
